import { ErrorBoundary } from '@sentry/react'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import { KeyRound, LifeBuoy, TicketPercent, Users } from 'lucide-react'
import { OfferLibrary } from './OfferLibrary'
import { CreateOfferPage } from './CreateOfferPage'
import { OfferDetailsPage } from './OfferDetailsPage'
import { CustomersPage } from './CustomersPage'
import { CustomerOfferPage } from './CustomerOfferPage'
import { RetentionOffersPage } from './RetentionOffersPage'
import { PromoCodesPage } from './PromoCodesPage'
import { OfferNotice } from './ui'

export function MobileOffersPage() {
    const { pathname, search } = useLocation()
    const customerTab = pathname.includes('/mobile-offers/customers')
    const retentionTab = pathname.includes('/mobile-offers/retention') || new URLSearchParams(search).get('purpose') === 'retention'
    const codesTab = pathname.includes('/mobile-offers/codes')
    return (
        <ErrorBoundary fallback={<OfferNotice error>Mobile offers could not be displayed. Reload before making changes.</OfferNotice>}>
            {/* shrink-0: the layout's content area is a fixed-height scrolling flex column; without it this
                page is squeezed to one screen and its background stops while taller content overflows. */}
            <div className="min-h-full shrink-0 bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:p-6 lg:p-8">
                <div className="mx-auto max-w-7xl space-y-6">
                    <header>
                        <h1 className="text-2xl font-semibold tracking-tight">Mobile offers</h1>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Create store offers. Give the right customers a reason to subscribe.</p>
                    </header>
                    <nav aria-label="Mobile offers" className="flex gap-6 overflow-x-auto border-b border-slate-200 dark:border-slate-700">
                        {[
                            { to: '/billing/mobile-offers', title: 'Offers', Icon: TicketPercent, active: !customerTab && !retentionTab && !codesTab },
                            { to: '/billing/mobile-offers/customers', title: 'Customers', Icon: Users, active: customerTab },
                            { to: '/billing/mobile-offers/retention', title: 'Retention', Icon: LifeBuoy, active: retentionTab },
                            { to: '/billing/mobile-offers/codes', title: 'Codes', Icon: KeyRound, active: codesTab },
                        ].map(({ to, title, Icon, active }) => (
                            <Link
                                key={to}
                                to={to}
                                aria-current={active ? 'page' : undefined}
                                className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${active ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'}`}
                            >
                                <Icon size={18} aria-hidden="true" />
                                {title}
                            </Link>
                        ))}
                    </nav>
                    <Routes>
                        <Route index element={<OfferLibrary />} />
                        <Route path="new" element={<CreateOfferPage />} />
                        <Route path="offers/:offerId" element={<OfferDetailsPage />} />
                        <Route path="customers" element={<CustomersPage />} />
                        <Route path="customers/:userId" element={<CustomerOfferPage />} />
                        <Route path="retention" element={<RetentionOffersPage />} />
                        <Route path="codes" element={<PromoCodesPage />} />
                        <Route path="*" element={<OfferNotice error>This mobile-offer page was not found.</OfferNotice>} />
                    </Routes>
                </div>
            </div>
        </ErrorBoundary>
    )
}
